import React from 'react';
import { Link } from 'react-router-dom';
import { Package, FileCheck, Warehouse, Truck, DollarSign, HeadphonesIcon } from 'lucide-react';

const Services = () => {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-light text-gray-900 mb-6">Our Services</h1>
            <p className="text-xl text-gray-600 font-light">
              Complete turnkey solution for international and domestic brands entering the Florida market
            </p>
          </div>
        </div>
      </section>

      {/* Import Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">Import Services</h2>
            <p className="text-lg text-gray-600 font-light">
              Complete import solutions for international brands entering the Florida market
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-10 border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <FileCheck size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4">Customs Clearance & Documentation</h3>
              <p className="text-gray-600 font-light mb-6">
                We handle all customs procedures and import documentation, ensuring your products clear customs smoothly and comply with all federal and state regulations. Our experienced team manages the entire process from arrival to warehouse delivery.
              </p>
              <ul className="space-y-3 text-gray-600 font-light">
                <li>• Complete customs documentation and filing</li>
                <li>• TTB permit management and compliance</li>
                <li>• Certificate of Label Approval (COLA) processing</li>
                <li>• Import license coordination</li>
                <li>• Tax and duty calculation and payment</li>
                <li>• FDA and USDA compliance for specialty products</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-10 border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <Warehouse size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4">Temperature-Controlled Warehousing</h3>
              <p className="text-gray-600 font-light mb-6">
                Our 4,500 sq. ft. state-licensed facility features climate-controlled storage specifically designed for wine and spirits. We maintain optimal temperature and humidity levels to preserve product quality and integrity from import to distribution.
              </p>
              <ul className="space-y-3 text-gray-600 font-light">
                <li>• Climate-controlled environment (55-65°F constant)</li>
                <li>• Secure, monitored storage facility</li>
                <li>• Real-time inventory tracking system</li>
                <li>• Professional product handling and organization</li>
                <li>• Fully licensed and insured storage</li>
                <li>• Regular quality inspections</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Distribution Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">Distribution Services</h2>
            <p className="text-lg text-gray-600 font-light">
              Reliable distribution network serving restaurants, hotels, and retailers across Florida
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-10 border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <Truck size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4">Statewide Delivery Network</h3>
              <p className="text-gray-600 font-light mb-6">
                Professional delivery service covering all major Florida markets. We ensure your products reach restaurants, hotels, and liquor stores on time and in perfect condition with our dedicated logistics team.
              </p>
              <ul className="space-y-3 text-gray-600 font-light">
                <li>• Florida-wide coverage including Miami, Tampa, Orlando, Jacksonville</li>
                <li>• Scheduled and on-demand deliveries</li>
                <li>• Temperature-controlled transport vehicles</li>
                <li>• Real-time delivery tracking and notifications</li>
                <li>• Professional handling and care</li>
              </ul>
            </div>

            <div className="bg-white p-10 border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <DollarSign size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4">Billing & Collections</h3>
              <p className="text-gray-600 font-light mb-6">
                Complete financial administration including professional invoicing, payment processing, and account receivables management. We handle all billing operations so you can focus on sales and growth.
              </p>
              <ul className="space-y-3 text-gray-600 font-light">
                <li>• Professional invoice generation and delivery</li>
                <li>• Payment processing and tracking</li>
                <li>• Account receivables management</li>
                <li>• Detailed financial reporting</li>
                <li>• Credit terms administration</li>
              </ul>
            </div>

            <div className="bg-white p-10 border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <Package size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4">Order Processing & Fulfillment</h3>
              <p className="text-gray-600 font-light mb-6">
                Efficient order management from receipt to delivery confirmation. Our streamlined fulfillment process ensures accurate, timely order processing with comprehensive quality control at every stage.
              </p>
              <ul className="space-y-3 text-gray-600 font-light">
                <li>• Fast order processing and confirmation</li>
                <li>• Professional pick, pack, and quality control</li>
                <li>• Inventory allocation and management</li>
                <li>• Order tracking and status updates</li>
                <li>• Delivery coordination and confirmation</li>
              </ul>
            </div>

            <div className="bg-white p-10 border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <HeadphonesIcon size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4">Account Management & Support</h3>
              <p className="text-gray-600 font-light mb-6">
                Dedicated account management providing strategic partnership and ongoing support. We work closely with you to optimize operations, provide market insights, and ensure seamless service delivery.
              </p>
              <ul className="space-y-3 text-gray-600 font-light">
                <li>• Dedicated account manager</li>
                <li>• Market intelligence and insights</li>
                <li>• Strategic business consultation</li>
                <li>• Proactive customer service</li>
                <li>• Performance reporting and analytics</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-16 text-center">
            <div>
              <div className="text-5xl font-light text-gray-900 mb-3">15+</div>
              <div className="text-gray-600 font-light">Years of Experience</div>
            </div>
            <div>
              <div className="text-5xl font-light text-gray-900 mb-3">4,500</div>
              <div className="text-gray-600 font-light">Sq. Ft. Storage Facility</div>
            </div>
            <div>
              <div className="text-5xl font-light text-gray-900 mb-3">100%</div>
              <div className="text-gray-600 font-light">Compliance Guaranteed</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light text-gray-900 mb-6">Ready to Enter the Florida Market?</h2>
          <p className="text-lg text-gray-600 font-light mb-10">
            Let us handle the logistics and compliance while you focus on building your brand and growing your sales.
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-gray-900 text-white font-light hover:bg-gray-800 transition"
            data-testid="contact-us-btn"
          >
            Contact Us Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;